import { looseToNumber } from '@develop/ia-utils'
import { color } from 'console-log-colors'
import { useProductDetails } from './product/product-details'
import type { RouteLocation } from '#vue-router'
import type { PartialPharmacyParams, PartialPharmacyProductParams, PharmacyParams, PharmacyProductParams, ProductParams } from '@/models/middleware-types'
import { IaCookie } from '~/models/cookie'
import type { DeactivatedPharmacyCookie } from '~/models/pharmacy'

type NamedRoute = RouteLocation['name']

export type PharmacyRouteValidationResult = {
  status: number
  name?: NamedRoute
  params?: PharmacyParams
}

type ProductRouteValidationResult = {
  status: number
  name?: NamedRoute
  params?: ProductParams | PharmacyProductParams
}

const getPharmacyParamsWithStatus = async (pharmacyId: number, pharmacyParams?: PartialPharmacyParams | PartialPharmacyProductParams) => {
  const currentPharmacy = useCurrentPharmacyStore()

  const { status, data: pharmacy } = await currentPharmacy.ensure(pharmacyId)

  // If pharmacy endpoint returns 404, 500 or 400, we need to return the same status
  // Any further checks are not needed
  if (status !== 200)
    return { status }

  if (currentPharmacy.isDeactivated) {
    const deactivatedPharmacyCookie = useCookie<DeactivatedPharmacyCookie>(IaCookie.DeactivatedPharmacyInfo)
    const id = currentPharmacy.pharmacyId
    const postcode = looseToNumber(currentPharmacy.pharmacy?.location.postcode)

    if (id && postcode)
      set(deactivatedPharmacyCookie, { id, postcode })

    // Unselect current pharmacy because it's deactivated
    // user needs to select another pharmacy on the apofinder
    currentPharmacy.reset()

    return {
      status: 302,
      name: 'apofinder',
      params: pharmacyParams,
    } as PharmacyRouteValidationResult
  }

  // Only the pharmacy id should be relevant to proceed
  const canonicalParams: PartialPharmacyParams = currentPharmacy.slugSegments ?? { pharmacyId: String(pharmacyId) }

  // If params are defined, we need to construct a pharmacy slug from them and compare it with the slug from the API response
  if (pharmacyParams && pharmacy) {
    if (!hasFullPharmacySlug(canonicalParams)) {
      if (pharmacyParams.pharmacyPlz) {
        return {
          status: 301,
          params: canonicalParams,
        } as PharmacyRouteValidationResult
      }
    } else {
      const toPharmacySlug = convertPharmacyParamsToSlug(pharmacyParams)

      const pharmacySlugMismatch = toPharmacySlug !== pharmacy.additionalInformation.slug
      if (pharmacySlugMismatch) {
        onShowLogs(() => console.log(`Corrected pharmacy slug: ${color.red(toPharmacySlug)} → ${color.green(pharmacy.additionalInformation.slug)}`))
        return {
          status: 301,
          params: canonicalParams,
        } as PharmacyRouteValidationResult
      }
    }
  }

  return {
    status: 200,
    params: canonicalParams,
  } as PharmacyRouteValidationResult
}

export const useRoutingValidator = () => {
  // TODO: Instead of 404 we should return 302 to the homepage
  const validateIncomingPharmacy = async (pharmacyParams: PartialPharmacyParams): Promise<PharmacyRouteValidationResult> => {
    const { pharmacyId: pharmacyIdSegment } = pharmacyParams

    const pharmacyId = looseToNumber(pharmacyIdSegment)

    if (!pharmacyId)
      return { status: 404 }

    return getPharmacyParamsWithStatus(pharmacyId, pharmacyParams)
  }

  const validateIncomingProduct = async (productParams: ProductParams & Partial<PharmacyParams>): Promise<ProductRouteValidationResult> => {
    const {
      pharmacyId: pharmacyIdSegment,
      pzn,
      productName: productNameSegment,
    } = productParams

    const pharmacyId = looseToNumber(pharmacyIdSegment)
    const isPharmacyProduct = pharmacyId !== undefined

    const { productDetails, meta, error: productDetailsError } = await useProductDetails(pzn, { pharmacyId })

    const product = productDetails.value

    if (productDetailsError.value || !product) {
      return { status: 404 }
    }

    // not sure if this case can happen, error should already be thrown, because isSuccess=false should be returned when details is not 200
    if (meta.value && meta.value?.status !== 200) {
      return {
        status: meta.value.status,
      }
    }

    // check if the incoming url is a pharmacy product url or just product url (without pharmacy)
    const productSlugMismatch = product.slug.product !== productNameSegment

    // fix product slug if it's not correct
    if (!isPharmacyProduct && productSlugMismatch) {
      onShowLogs(() => console.log(`Corrected product slug: ${color.red(productNameSegment)} → ${color.green(product.slug.product)}`))
      return {
        status: 301,
        params: {
          productName: product.slug.product,
          pzn,
        },
      }
    }

    // if it's a pharmacy product url, we need to check if the pharmacy slug is correct
    if (isPharmacyProduct && pharmacyIdSegment) {
      // in case short pharmacy route is used, this will still return full pharmacy slug params, so this automatically handles short pharmacy route for product pages
      const pharmacyCheckResult = await getPharmacyParamsWithStatus(pharmacyId, {
        ...productParams,
        pharmacyId: pharmacyIdSegment,
      })

      // fix mismatched product slug, or pharmacy slug
      if ((productSlugMismatch || pharmacyCheckResult.status === 301) && pharmacyCheckResult.params) {
        onShowLogs(() => {
          if (productSlugMismatch) {
            console.log(`Corrected product slug: ${color.red(productNameSegment)} → ${color.green(product.slug.product)}`)
          }
        })
        return {
          status: 301,
          name: resolvePharmacyRouteName('pharmacyName-pharmacyPlz-pharmacyId-produkt-productName-pzn', pharmacyCheckResult.params),
          params: {
            ...pharmacyCheckResult.params,
            productName: product.slug.product,
            pzn: product.pzn,
          },
        }
      }

      if (pharmacyCheckResult.status === 302) {
        return {
          name: pharmacyCheckResult.name,
          status: pharmacyCheckResult.status,
        }
      }

      if (pharmacyCheckResult.status !== 200) {
        return {
          status: pharmacyCheckResult.status,
        }
      }
    }

    return { status: 200 }
  }

  return {
    validateIncomingPharmacy,
    validateIncomingProduct,
    getPharmacyParamsWithStatus,
  }
}
